import { Injectable, computed, inject, signal } from '@angular/core';
import { AuthService } from '../../../modules/auth/services/auth/auth.service';
import {
  CpSubscriptionPlanOption,
  CpSubscriptionService,
  CpSubscriptionSummary,
} from '../../../services/cp-subscription/cp-subscription.service';

@Injectable({ providedIn: 'root' })
export class CpSubscriptionTrialService {
  private readonly api = inject(CpSubscriptionService);
  private readonly auth = inject(AuthService);

  private readonly summarySig = signal<CpSubscriptionSummary | null>(null);
  private readonly plansSig = signal<CpSubscriptionPlanOption[]>([]);
  private readonly loadingSig = signal(false);
  private readonly upgradingPlanIdSig = signal<string | null>(null);

  readonly summary = this.summarySig.asReadonly();
  readonly plans = this.plansSig.asReadonly();
  readonly loading = this.loadingSig.asReadonly();
  readonly upgradingPlanId = this.upgradingPlanIdSig.asReadonly();

  readonly requiresUpgrade = computed(
    () => this.summarySig()?.requiresUpgrade === true,
  );

  readonly trialDaysRemaining = computed(
    () => this.summarySig()?.trial?.daysRemaining ?? null,
  );

  refresh(): void {
    if (!this.auth.userDetails()) {
      return;
    }
    this.loadingSig.set(true);
    this.api.getSummary().subscribe({
      next: (summary) => {
        this.summarySig.set(summary);
        const entitlements =
          summary.menuEntitlements ?? summary.entitlements ?? [];
        if (entitlements.length) {
          this.auth.updateEntitlements(entitlements);
        }
        this.loadingSig.set(false);
      },
      error: () => {
        this.loadingSig.set(false);
      },
    });
    this.api.listPlans().subscribe({
      next: (plans) => this.plansSig.set(plans),
    });
  }

  markUpgradeCompleted(entitlements: string[]): void {
    this.summarySig.update((summary) =>
      summary
        ? {
            ...summary,
            requiresUpgrade: false,
            trial: {
              ...summary.trial,
              trialExpired: false,
              requiresUpgrade: false,
            },
            entitlements:
              entitlements.length > 0 ? entitlements : summary.entitlements,
            menuEntitlements:
              entitlements.length > 0
                ? entitlements
                : summary.menuEntitlements,
          }
        : summary,
    );
  }

  clearUpgrading(): void {
    this.upgradingPlanIdSig.set(null);
  }

  setUpgrading(planId: string | null): void {
    this.upgradingPlanIdSig.set(planId);
  }
}
